import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { components } from '../api/tipos'
import type { FiltrosAvisos } from './useFiltrosAvisos'

/**
 * El tipo generado del OpenAPI no incluye aún `notas` (campo de texto libre de
 * B.11). Se extiende aquí para no editar el archivo generado, que se regenera.
 */
export type AvisoResumen = components['schemas']['AvisoResumen'] & {
  notas?: string | null
}
export type PaginaAvisos = components['schemas']['Pagina_AvisoResumen_']
/**
 * Contadores por nivel (bien/atencion/critico) para la botonera de la opción C.
 * El schema generado no los incluye (la respuesta no está tipada en OpenAPI),
 * así que se extiende aquí.
 */
export type ResumenAvisos = components['schemas']['ResumenAvisos'] & {
  nivel_bien?: number
  nivel_atencion?: number
  nivel_critico?: number
}

/**
 * 25 por página: una lista corta que se revisa sin scroll infinito (el usuario
 * lo pidió explícitamente). La paginación lleva al resto.
 */
export const TAMANO_PAGINA = 25

export function useAvisos(filtros: FiltrosAvisos, tamanoPagina: number = TAMANO_PAGINA) {
  const api = useApi()

  return useQuery<PaginaAvisos, ErrorApi>({
    // `filtros` entero en la clave: al cambiar de pagina o de filtro la clave
    // cambia y TanStack Query no puede servir el resultado anterior.
    queryKey: ['avisos', filtros, tamanoPagina],
    queryFn: ({ signal }) =>
      api.pedir<PaginaAvisos>('/api/avisos', {
        parametros: {
          estado: filtros.estado,
          semanas_minimas: filtros.semanas_minimas,
          nivel: filtros.nivel,
          orden: filtros.orden,
          // El buscador se llama `q` en la API; el cliente omite el vacio.
          q: filtros.q,
          page: filtros.page,
          page_size: tamanoPagina,
        },
        senal: signal,
      }),
  })
}

/**
 * Las metricas de cabecera, en su propia consulta. Aparte del listado a
 * proposito: describen la seccion entera y no cambian al filtrar ni al pasar de
 * pagina, asi que atarlas al listado las recargaria sin motivo y, peor, un
 * fallo de una se llevaria por delante a la otra.
 */
export function useResumenAvisos() {
  const api = useApi()

  return useQuery<ResumenAvisos, ErrorApi>({
    queryKey: ['avisos', 'resumen'],
    queryFn: ({ signal }) => api.pedir<ResumenAvisos>('/api/avisos/resumen', { senal: signal }),
  })
}

/** Invalida el listado y el resumen de avisos tras una acción (B.11). */
function useInvalidarAvisos() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: ['avisos'] })
}

/** B.11 — marca la gestión como realizada (estado avisado) + nota opcional. */
export function useGestionarAviso() {
  const api = useApi()
  const invalidar = useInvalidarAvisos()
  return useMutation<unknown, ErrorApi, { id: string; nota?: string }>({
    mutationFn: ({ id, nota }) =>
      api.pedir(`/api/avisos/${id}/gestionar`, { metodo: 'POST', cuerpo: { nota } }),
    onSuccess: invalidar,
  })
}

/** B.11 — guarda la nota de texto libre de un aviso (histórico). */
export function useGuardarNotaAviso() {
  const api = useApi()
  const invalidar = useInvalidarAvisos()
  return useMutation<unknown, ErrorApi, { id: string; nota: string }>({
    mutationFn: ({ id, nota }) =>
      api.pedir(`/api/avisos/${id}/nota`, { metodo: 'POST', cuerpo: { nota } }),
    onSuccess: invalidar,
  })
}

/** B.11 — da de baja al cliente desde Avisos y lo quita de la lista. */
export function useDarDeBajaAviso() {
  const api = useApi()
  const invalidar = useInvalidarAvisos()
  return useMutation<unknown, ErrorApi, { id: string; hasta?: string }>({
    mutationFn: ({ id, hasta }) =>
      api.pedir(`/api/avisos/${id}/baja`, { metodo: 'POST', cuerpo: { hasta } }),
    onSuccess: invalidar,
  })
}
